import { Bullseye, EmptyState, EmptyStateBody, EmptyStateVariant, Spinner } from "@patternfly/react-core";
import { ErrorCircleOIcon, SearchIcon } from "@patternfly/react-icons";
import {
	ExpandableRowContent,
	type ISortBy,
	Table,
	type TableVariant,
	Tbody,
	Td,
	Th,
	Thead,
	type ThProps,
	Tr,
} from "@patternfly/react-table";
import { isEmpty, map, orderBy } from "lodash";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";
import { useTableContext } from "./TableContext";

export interface ITableRow {
	_id: string | number;
	selected?: boolean;
	disabled?: boolean;
	[key: string]: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table component accepts any row shape
export interface IColumn {
	title: string;
	accessor: (data: any) => string | number | ReactNode;
	extraProps?: { column?: Record<string, unknown>; row?: Record<string, unknown> };
	sortable?: boolean;
	cell?: (data: any) => string | number | ReactNode;
	colText?: string;
	key?: string;
}

export interface PFTableProps {
	aria?: string;
	isLoading?: boolean;
	error?: string;
	onSelect?: (rowId: string | number, isSelected: boolean) => void;
	selectableRows?: boolean;
	emptyStateTitle?: string | ReactNode;
	emptyStateBody?: string | ReactNode;
	emptyStateComponent?: ReactNode;
	selectVariant?: string;
	totalItems?: number;
	//method to filter out the results based on search value
	filterCriteria?: (data: ITableRow, search: string) => boolean;
	onSOrt?: (index: number, direction: "asc" | "desc") => void;
	initialSortByData?: { index: number; direction: "asc" | "desc" };
	variant?: TableVariant | "compact";
	isExpandable?: boolean;
	expandedRows?: number[];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table component accepts any row shape
	childRow?: (data: any) => ReactNode;
	nestedOffsetColCount?: number;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic table component accepts any row shape
	rowStyleResolver?: (data: any) => string | null;
	noPadding?: boolean;
}

const RowPlaceHoldWrapper = ({ children }: { children: ReactNode }) => (
	<Tbody>
		<Tr className="row-placeholder">
			<Td colSpan={10}>
				<Bullseye>{children}</Bullseye>
			</Td>
		</Tr>
	</Tbody>
);

const errorRow = (error: string) => (
	<RowPlaceHoldWrapper>
		<EmptyState
			headingLevel="h5"
			icon={ErrorCircleOIcon}
			titleText={error}
			variant={EmptyStateVariant.full}
		></EmptyState>
	</RowPlaceHoldWrapper>
);

const emptyStateRow = (title?: string | ReactNode, body?: string | ReactNode) => (
	<RowPlaceHoldWrapper>
		{" "}
		<EmptyState
			headingLevel="h2"
			icon={SearchIcon}
			titleText={title ? title : "No results found"}
			variant={EmptyStateVariant.sm}
		>
			<EmptyStateBody>{body ? body : null}</EmptyStateBody>
		</EmptyState>
	</RowPlaceHoldWrapper>
);

const loadingStateRow = (
	<RowPlaceHoldWrapper>
		<EmptyState variant={EmptyStateVariant.sm}>
			<Spinner />
		</EmptyState>
	</RowPlaceHoldWrapper>
);

export const PFTableV2 = (props: PFTableProps) => {
	const {
		columns,
		rows,
		actionResolver,
		currentPage,
		perPage,
		setSelectedPage,
		searchValue,
		searchFunction,
		disableBuiltInPagination,
	} = useTableContext();
	const [sortBy, setSortBy] = useState<ISortBy>(props.initialSortByData || {});
	const [dataRows, setDataRows] = useState<ITableRow[]>(rows);
	const [expandedRows, setExpandedRows] = useState<number[]>([]);
	const filterQuery = useRef("");

	useEffect(() => {
		if (filterQuery.current !== searchValue) {
			searchFunction?.(searchValue, undefined, undefined, setSelectedPage);
		}
		filterQuery.current = searchValue;
		setDataRows(rows);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows, searchValue, searchFunction, setSelectedPage]);

	const onSelect = (
		_event: React.FormEvent<HTMLInputElement>,
		isSelected: boolean,
		rowId: string | number,
		selectAll?: boolean,
	) => {
		let rows;
		if (selectAll) {
			rows = map(dataRows, (row) => {
				row.selected = isSelected;
				return row;
			});
		} else {
			rows = [...dataRows];
			const index = rows.findIndex((el) => el._id === rowId);
			rows[index].selected = isSelected;
		}
		setDataRows(rows);
		props.onSelect?.(rowId, isSelected);
	};

	const onSort = (_event: React.MouseEvent, index: number, direction: "asc" | "desc") => {
		setSortBy({
			index,
			direction,
		});
		if (props.onSOrt) {
			props.onSOrt(index, direction);
		} else {
			const sorter = (prop: ITableRow) => {
				const value = columns[index]?.accessor(prop);
				return typeof value === "string" ? value.toLowerCase() : value;
			};
			const sortedRows = orderBy(dataRows, [sorter], [direction]);
			setDataRows(sortedRows);
		}
	};

	const getSortParams = (columnIndex: number): NonNullable<ThProps["sort"]> => ({
		sortBy: sortBy,
		onSort: onSort,
		columnIndex,
	});

	const getTableRowsData = (rows: ITableRow[]) => {
		if (!disableBuiltInPagination) {
			rows = rows?.slice((currentPage - 1) * perPage, currentPage * perPage);
		}
		const tableRows = rows.map((item: ITableRow, rowIndex: number) => {
			const resolvedClassNames = props?.rowStyleResolver?.(item) || undefined;
			const tableRow = (
				<Fragment key={`row-${rowIndex}`}>
					<Tr {...(resolvedClassNames && { className: resolvedClassNames })}>
						{props.selectableRows && (
							<Td
								select={{
									rowIndex,
									onSelect: (_event, isSelecting) => onSelect(_event, isSelecting, item._id),
									isSelected: item.selected ?? false,
									...(item.disabled !== undefined ? { isDisabled: item.disabled } : {}),
								}}
							/>
						)}
						{props.isExpandable && (
							<Td
								{...(props.expandedRows?.includes(rowIndex)
									? {
											expand: {
												rowIndex,
												isExpanded: expandedRows.includes(rowIndex),
												onToggle: () => handleRowToggle(rowIndex),
											},
										}
									: {})}
							/>
						)}
						{columns.map((col, index: number) => (
							<Td key={index} dataLabel={col.colText || col.title} {...(col.extraProps?.row || {})}>
								{col?.cell ? col.cell(item) : col?.accessor ? col.accessor(item) : ""}
							</Td>
						))}
						{actionResolver && <Td key={`action-${rowIndex}`} actions={{ items: actionResolver(item, {}) }} />}
					</Tr>
					{props.isExpandable ? (
						<Tr key={`expandable-${rowIndex}`} isExpanded={expandedRows.includes(rowIndex)}>
							{!!props.nestedOffsetColCount &&
								[...Array(props.nestedOffsetColCount)].map((count: number) => (
									<Td key={`offset-nested-col-${rowIndex}-${count}`} />
								))}
							<Td
								{...(props.noPadding !== undefined ? { noPadding: props.noPadding } : {})}
								colSpan={columns.length + (actionResolver ? 2 : 1)}
								className="child-content-row"
							>
								<ExpandableRowContent>{props.childRow ? props.childRow(item) : null}</ExpandableRowContent>
							</Td>
						</Tr>
					) : null}
				</Fragment>
			);
			//the blue left line doesn't come while expanded if each Tr is not wrapped TBody
			return props.isExpandable ? (
				<Tbody key={`expandable-tbody-${rowIndex}`} isExpanded={expandedRows.includes(rowIndex)}>
					{tableRow}
				</Tbody>
			) : (
				tableRow
			);
		});
		//for non expandable types we need only one Tbody for all the Tr
		return props.isExpandable ? tableRows : <Tbody>{tableRows}</Tbody>;
	};

	const getTableRows = () => {
		return props.isLoading ? (
			loadingStateRow
		) : props.error ? (
			errorRow(props.error)
		) : isEmpty(dataRows) ? (
			props.emptyStateComponent ? (
				<RowPlaceHoldWrapper>{props.emptyStateComponent}</RowPlaceHoldWrapper>
			) : (
				emptyStateRow(props.emptyStateTitle, props.emptyStateBody)
			)
		) : (
			getTableRowsData(dataRows)
		);
	};

	const handleRowToggle = (rowIndex: number) => {
		if (expandedRows.includes(rowIndex)) {
			setExpandedRows(expandedRows.filter((item: number) => item !== rowIndex));
		} else {
			setExpandedRows([...expandedRows, rowIndex]);
		}
	};

	return (
		<Table
			aria-label={props?.aria || "Actions table"}
			{...(props.variant !== undefined ? { variant: props.variant } : {})}
		>
			<Thead>
				<Tr>
					{props.selectableRows && <Th />}
					{props.isExpandable && <Th />}
					{columns.map((col, colIndex) => {
						const sortProp = col.sortable ? { sort: getSortParams(colIndex) } : {};
						return (
							<Th key={`table-head-${colIndex}`} {...(col.extraProps?.column || {})} {...sortProp}>
								{col.title}
							</Th>
						);
					})}
					{actionResolver && <Th screenReaderText="Row Actions" />}
				</Tr>
			</Thead>
			{getTableRows()}
		</Table>
	);
};
